<script>
    import { onMount } from 'svelte';
    import { getApiUrl } from '../api';
    import { authFetch } from '../authService';

    let logs = [];
    let total = 0;
    let page = 0;
    let isLoading = true;

    async function loadLogs() {
        isLoading = true;
        try {
            const res = await authFetch(getApiUrl(`/admin/recommendation-logs?page=${page}`));
            const data = await res.json();
            logs = data.logs;
            total = data.total;
        } catch (e) { console.error(e); }
        finally { isLoading = false; }
    }

    onMount(loadLogs);

    function next() { if ((page + 1) * 10 < total) { page++; loadLogs(); } }
    function prev() { if (page > 0) { page--; loadLogs(); } }
</script>

<div class="admin-container">
    <h2>AI Recommendation Query Logs</h2>
    <p>Viewing page {page + 1} of {Math.ceil(total / 10) || 1} ({total} total queries)</p>

    <div class="table-wrapper">
        <table>
            <thead>
                <tr>
                    <th>Time</th>
                    <th>User / IP</th>
                    <th>Context</th>
                    <th>Matched DB Candidates</th>
                    <th>Recommendation</th>
                </tr>
            </thead>
            <tbody>
                {#each logs as log}
                    <tr>
                        <td>{new Date(log.createdAt).toLocaleString()}</td>
                        <td>
                            <div class="uid">
                                <a href={`/admin/users/${log.userId}`} target="_blank" class="pid-link" title="View User ID: {log.userId}">{log.userId}</a>
                            </div>
                            <div class="ip">{log.ip}</div>
                        </td>
                        <td>
                            <div class="spending-badge">💰 ${log.targetSpending}</div>
                            {#if log.storeName}
                                <div class="store-badge-bg">🏪 {log.storeName}</div>
                            {/if}
                            {#if log.categories && log.categories.length > 0}
                                <div class="badge category">🏷️ {log.categories.join(', ')}</div>
                            {/if}
                            {#if log.ownedCards && log.ownedCards.length > 0}
                                <div class="badge cards">💳 {log.ownedCards.join(', ')} Cards</div>
                            {/if}
                            {#if log.ownedPaymentMethods && log.ownedPaymentMethods.length > 0}
                                <div class="badge methods">📱 {log.ownedPaymentMethods.join(', ')} Methods</div>
                            {/if}
                        </td>
                        <td>
                            {#if log.matchedPromoIds && log.matchedPromoIds.length > 0}
                                <div class="matched-count">{log.matchedPromoIds.length} candidate(s)</div>
                                <div class="matched-links">
                                    {#each log.matchedPromoIds as pid}
                                        <a href={`/promotions/detail/${pid}`} target="_blank" class="pid-link" title="View Promotion ID: {pid}">{pid.substring(0,6)}...</a>
                                    {/each}
                                </div>
                            {:else}
                                <span class="none">0 found</span>
                            {/if}
                        </td>
                        <td>
                            <div class="promo">{log.promoTitle}</div>
                            {#if log.recommendedId}
                                <a href={`/promotions/detail/${log.recommendedId}`} target="_blank" class="small-link">{log.recommendedId}</a>
                            {/if}
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>

    <div class="pagination">
        <button on:click={prev} disabled={page === 0}>Previous</button>
        <button on:click={next} disabled={(page + 1) * 10 >= total}>Next</button>
    </div>
</div>

<style>
    .admin-container { padding: 2rem; max-width: 1400px; margin: auto; }
    .table-wrapper { background: white; border-radius: 8px; overflow-x: auto; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
    table { width: 100%; border-collapse: collapse; min-width: 1000px; }
    th, td { padding: 1rem; text-align: left; border-bottom: 1px solid #eee; vertical-align: top; }
    th { background: #f8f9fa; font-weight: 600; }
    
    .uid { font-weight: bold; font-size: 0.85rem; word-break: break-all; max-width: 200px; }
    .ip { font-size: 0.75rem; color: #777; }
    
    .spending-badge { font-weight: bold; color: #28a745; margin-bottom: 6px; font-size: 0.95rem; }
    .store-badge-bg { background: #e3f2fd; color: #0d47a1; padding: 2px 6px; border-radius: 4px; display: inline-block; margin-bottom: 4px; font-size: 0.8rem; font-weight: 500; }
    .badge { background: #f1f3f5; color: #495057; padding: 2px 6px; border-radius: 4px; display: inline-block; margin-bottom: 4px; font-size: 0.8rem; }
    
    /* Matched Candidates DB list */
    .matched-count { font-size: 0.8rem; font-weight: bold; color: #555; margin-bottom: 0.5rem; }
    .matched-links { display: flex; flex-wrap: wrap; gap: 6px; max-width: 250px; }
    .pid-link { background: #f8f9fa; border: 1px solid #ddd; padding: 3px 6px; border-radius: 4px; font-size: 0.75rem; text-decoration: none; color: #007bff; font-family: monospace; transition: background 0.2s;}
    .pid-link:hover { background: #e9ecef; border-color: #007bff; }
    
    .promo { color: #007bff; font-weight: 600; margin-bottom: 4px; }
    .small-link { font-size: 0.8rem; color: #666; text-decoration: underline; }
    .none { color: #999; font-style: italic; font-size: 0.85rem; }

    .pagination { margin-top: 1.5rem; display: flex; gap: 1rem; justify-content: center; }
    button { padding: 0.5rem 1.2rem; cursor: pointer; border: 1px solid #ccc; border-radius: 4px; background: white; font-weight: 500;}
    button:disabled { opacity: 0.5; cursor: not-allowed; }
    button:hover:not(:disabled) { background: #f8f9fa; }
</style>